'use client';
import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { Input } from '@shared/components/ui/input';
import { Button } from '@shared/components/ui/button';
import { DateRangePicker } from '@shared/components/ui/DateRangePicker';
import { cn } from '@shared/utils/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePreInvoicesFilters } from '../../hooks/usePreInvoicesFilters';

interface PreInvoicesFiltersPanelProps {
  initialConditions?: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
  className?: string;
}

const FilterField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-muted-foreground">{label}</label>
    {children}
  </div>
);

/**
 * Panel de filtros del módulo Prefacturas (lista).
 * Estado, fecha de creación y código.
 */
export const PreInvoicesFiltersPanel = ({
  initialConditions = [],
  onChange,
  className,
}: PreInvoicesFiltersPanelProps) => {
  const { t } = useTranslation('preInvoicesModule');

  const { filters, handleStatusChange, handleCreatedDateRangeChange, handleCodeChange, clearAllFilters } =
    usePreInvoicesFilters({ initialConditions, onChange });

  const statusOptions = useMemo(
    () => [
      { label: t('filters.notInvoiced', 'No facturado'), value: 'DRAFT' },
      { label: t('filters.invoiced', 'Facturado'), value: 'CLOSED' },
    ],
    [t],
  );

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.status) count++;
    if (filters.createdDateRange.from && filters.createdDateRange.to) count++;
    if (filters.code.trim()) count++;
    return count;
  }, [filters]);

  return (
    <div className={cn('space-y-5', className)}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FilterField label={t('filters.status', 'Estado')}>
          <Combobox
            options={statusOptions}
            value={filters.status}
            setValue={handleStatusChange}
            placeholder={t('filters.select_placeholder', 'Seleccionar...')}
            dataTestId="filter-status"
          />
        </FilterField>

        <FilterField label={t('table.created_at', 'Fecha')}>
          <DateRangePicker
            initialDateFrom={filters.createdDateRange.from}
            initialDateTo={filters.createdDateRange.to}
            onUpdate={({ range }) => handleCreatedDateRangeChange(range)}
            placeholder={t('filters.dateRangePlaceholder', 'Seleccionar rango de fechas')}
            dataTestId="filter-created-at"
            triggerClassName="h-[38px] text-sm"
          />
        </FilterField>

        <FilterField label={t('table.code', 'Código')}>
          <Input
            value={filters.code}
            onChange={(e) => handleCodeChange(e.target.value)}
            placeholder={t('search.placeholder', 'Buscar prefactura...')}
            className="h-[38px]"
            data-testid="filter-code"
          />
        </FilterField>
      </div>

      {activeFiltersCount > 0 && (
        <div className="flex justify-end pt-3 border-t border-card-border">
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={clearAllFilters}
            type="button"
            data-testid="pre-invoices-clear-filters"
          >
            {t('filters.clear_filters', 'Limpiar filtros')} ({activeFiltersCount})
          </Button>
        </div>
      )}
    </div>
  );
};
